import { useEffect, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button } from 'react-bootstrap';
import { ModelIcon } from '../ModelIcon';
import { MarkdownContent } from './MarkdownContent';

interface AiChatModalProps {
  open: boolean;
  onClose: () => void;
  promptText: string;
  responseText: string;
  modelLabel?: string;
  modelIconSvg?: string | null;
}

export function AiChatModal({
  open,
  onClose,
  promptText,
  responseText,
  modelLabel,
  modelIconSvg,
}: AiChatModalProps) {
  const responderName = modelLabel?.trim() || 'AI';
  const [copied, setCopied] = useState<'prompt' | 'response' | null>(null);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  async function copy(kind: 'prompt' | 'response', value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(kind);
    window.setTimeout(() => setCopied(null), 1500);
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      size="lg"
      dialogClassName="modal-dialog-scrollable tracker-ai-chat-modal"
    >
      <style>{`
        .app-modal-dialog.modal-dialog.tracker-ai-chat-modal {
          --falcon-modal-width: 700px;
          width: min(700px, calc(100vw - 2rem));
          max-width: min(700px, calc(100vw - 2rem));
        }
        .tracker-ai-chat-modal-body {
          max-height: 70vh;
          overflow-y: auto;
          padding-bottom: 2rem;
        }
        .tracker-ai-chat-modal-content {
          padding-bottom: 1.25rem;
        }
        .tracker-ai-chat-response {
          background-color: color-mix(in srgb, var(--falcon-body-bg) 88%, #000 12%);
          border-color: color-mix(in srgb, var(--falcon-body-bg) 80%, #000 20%) !important;
        }
      `}</style>
      <Modal.Header closeButton className="align-items-center">
        <div className="d-flex align-items-center justify-content-between gap-3 min-w-0 flex-grow-1 me-2">
          <div className="min-w-0">
            <p className="text-truncate fs-0 fw-semibold text-1000 mb-0">جزئیات گفتگو</p>
          </div>
          {modelLabel ? (
            <div className="d-inline-flex align-items-center gap-2 rounded-3 border border-200 bg-100 dark__bg-1100 px-2 py-1 flex-shrink-0">
              <ModelIcon
                svg={modelIconSvg}
                name={responderName}
                size={26}
                className="rounded-2 border border-200 bg-white dark__bg-1000"
              />
              <span className="fs--1 fw-semibold text-800 text-nowrap">{responderName}</span>
            </div>
          ) : null}
        </div>
      </Modal.Header>

      <Modal.Body className="tracker-ai-chat-modal-body">
        <div className="d-flex flex-column gap-4 tracker-ai-chat-modal-content">
          <div className="d-flex justify-content-start">
            <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0" style={{ maxWidth: '100%' }}>
              <div className="d-flex align-items-center justify-content-between gap-2 px-1">
                <p className="fs--2 fw-medium text-500 mb-0">جستجوی شما</p>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="fs--2 text-500 p-0 text-decoration-none"
                  onClick={() => void copy('prompt', promptText)}
                >
                  {copied === 'prompt' ? 'کپی شد' : 'کپی'}
                </Button>
              </div>
              <div className="rounded-3 bg-primary px-3 py-2 fs--1 text-white shadow-sm">
                <p className="mb-1 fs--2 fw-medium text-white-50">این را سرچ کردی</p>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {promptText}
                </p>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <div className="d-flex align-items-start gap-2 flex-grow-1 min-w-0" style={{ maxWidth: '100%' }}>
              <div className="min-w-0 flex-grow-1">
                <div className="mb-1 d-flex align-items-center justify-content-between gap-2 px-1">
                  <p className="fs--2 fw-medium text-500 mb-0">پاسخ {responderName}</p>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="fs--2 text-500 p-0 text-decoration-none"
                    onClick={() => void copy('response', responseText)}
                  >
                    {copied === 'response' ? 'کپی شد' : 'کپی'}
                  </Button>
                </div>
                <div className="rounded-3 border bg-100 dark__bg-1100 px-3 py-2 fs--1 text-800 shadow-sm">
                  <MarkdownContent text={responseText} />
                </div>
              </div>
              <span className="flex-shrink-0 align-self-start" aria-hidden>
                <ModelIcon
                  svg={modelIconSvg}
                  name={responderName}
                  size={32}
                  className="rounded-circle border border-200 bg-white dark__bg-1000 shadow-sm"
                />
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
